<template>
  <SrpModal
    :isVisible="_isVisible"
    size="large"
    :isClosable="false"
    :isCloseButtonVisible="!isSaving"
    @close="
      () => {
        _isVisible = false;
        emit('close');
      }
    "
  >
    <template #content>
      <ChoosePhotosStep
        v-show="currentStep === WizardSteps.ChoosePhotos"
        :allPhotos="allPhotos"
        :customerId="props.customerId"
        @imageAddedToGallery="imageId => allPhotos.unshift(imageId)"
        v-model:checkedFileIds="checkedFileIds"
        :checkedFilesLimit="checkedFilesLimit"
      />

      <GeneratePostStep
        v-show="currentStep === WizardSteps.GeneratePost"
        v-model:selectedPostCount="selectedPostCount"
        v-model:userGuidance="userGuidance"
        :photoCount="checkedFileIds.length || allPhotos.length"
        @selectTemplate="validateAndGoToNextStep"
      />
    </template>

    <template #footer>
      <div v-if="checkedFileIds.length > checkedFilesLimit" class="global-error-string" style="margin-right: 12px">Max number of selected files is {{ checkedFilesLimit }}</div>
      <SrpButton
        v-if="currentStep === 1"
        color="gray"
        fill="outlined"
        style="margin-right: 12px"
        @click="
          () => {
            _isVisible = false;
            emit('close');
          }
        "
        :isDisabled="isSaving"
      >
        Cancel
      </SrpButton>

      <SrpButton v-else color="gray" fill="outlined" style="margin-right: 12px" @click="currentStep -= 1" :isDisabled="isSaving"> Back </SrpButton>

      <SrpButton @click="validateAndGoToNextStep" :isDisabled="currentStep === WizardSteps.ChoosePhotos && allPhotos.length === 0">
        {{ nextButtonName }}
        <template v-if="nextIconName" #icon><IconEmbedded :name="nextIconName" :size="19" /></template>
      </SrpButton>
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";

// Components
import SrpModal from "@components/ui/SrpModal.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
// steps
import ChoosePhotosStep from "./ChoosePhotosStep.vue";
import GeneratePostStep from "./GeneratePostStep.vue";
import axios from "axios";
import { SocialMediaPostData } from "@/contracts/shareStop";
import { SocialPostGenResult, SocialPostGenResultPayload } from "@/contracts/shareStop";

const emit = defineEmits<{
  (e: "close"): void;
  (e: "generatingStarted", postCount: number): void;
  (e: "generatingCompleted", payload: SocialPostGenResultPayload): void;
}>();

// Post generation state
const selectedPostCount = ref<number>(1);
const userGuidance = ref<string>("");

// Checked files limit ========================================================
const checkedFilesLimit = 500; // the AI limits how many images can be sent

// Toggle modal ===============================================================
const _isVisible = ref<boolean>(true);

// Change steps ===============================================================
enum WizardSteps {
  ChoosePhotos = 1,
  GeneratePost = 2,
}
const currentStep = ref<number>(WizardSteps.ChoosePhotos);

// Define props
const props = defineProps<{
  customerId: string;
}>();

// Checked files ==============================================================
const checkedFileIds = ref<Array<string>>([]);

// Load Photos ==============================================================
const allPhotos = ref<string[]>([]);
onMounted(async () => {
  const photoResult = await axios.get(`${import.meta.env.VITE_API_URL}/photo-gallery/customer/${props.customerId}`);
  allPhotos.value = photoResult.data.photoIds;
});

// Computed properties for button text and icon ===============================================
const nextButtonName = computed(() => {
  if (currentStep.value === WizardSteps.ChoosePhotos) {
    if (allPhotos.value.length === 0) return "Upload some photos";
    else if (checkedFileIds.value.length > 0) return `Use ${checkedFileIds.value.length} selected photos`;
    else return `Use ${allPhotos.value.length} existing photos`;
  } else if (currentStep.value === WizardSteps.GeneratePost) {
    return "Generate!";
  } else {
    return "Next";
  }
});

const nextIconName = computed(() => {
  return currentStep.value === WizardSteps.GeneratePost ? "ai_3" : "";
});

// Validate and go to next step ===============================================
async function validateAndGoToNextStep() {
  if (currentStep.value === WizardSteps.ChoosePhotos) {
    // Calculate post count based on number of photos
    const photoCount = checkedFileIds.value.length || allPhotos.value.length;
    selectedPostCount.value = photoCount > 50 ? 3 : photoCount > 20 ? 2 : 1;
  }
  if (currentStep.value === WizardSteps.GeneratePost) {
    genSocialPosts();
    _isVisible.value = false;
    return;
  }

  currentStep.value += 1;
}

const isSaving = ref<boolean>(false);
const socialPostPreviews = ref<Array<SocialPostGenResult>>([]);
async function genSocialPosts() {
  isSaving.value = true;
  socialPostPreviews.value = [];
  try {
    const postCount = selectedPostCount.value;
    emit("generatingStarted", postCount);

    const url = `${import.meta.env.VITE_API_URL}/social-generator/${props.customerId}/selected-assets/v2`;
    const payload = {
      assetIds: checkedFileIds.value || [],
      userGuidance: userGuidance.value,
      postCount: postCount,
      saveResults: true,
    };
    const { data } = await axios.post<SocialPostGenResultPayload>(url, payload);
    socialPostPreviews.value = data.results;
    emit("generatingCompleted", data);
  } finally {
    isSaving.value = false;
  }
}
</script>

<style scoped lang="scss"></style>
